"use client";

import React from "react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Box, Typography } from "@mui/material";
import CustomBreadcrumbs from "@/main/category/(components)/custom-breadcrumbs";
import { decode } from "html-entities";
import Link from "@/components/core/localized-mui-link";

interface BrandItem {
  manufacturer_id: string;
  name: string;
  image: string;
}

interface BrandsProps {
  items: BrandItem[];
}

const BrandsPage = ({ data }: { data: BrandsProps }) => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const getBrandKey = (item: BrandItem, index: number) =>
    `${item.manufacturer_id}-${item.name}-${index}`;

  const groupedData = data?.items?.reduce((acc: Record<string, BrandItem[]>, item: BrandItem) => {
    const firstChar = (item.name?.charAt(0) || "").toUpperCase();
    const groupKey = /^[0-9]/.test(firstChar) ? "0-9" : firstChar;

    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(item);
    return acc;
  }, {});

  const handleScroll = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <main className="md:container">
      <div className="flex flex-col xs:pt-[75px] md:pt-0">
        <div className="w-full">
          <ul className="pb-2.5 xs:px-[15px] md:px-0 md:pb-[25px]">
            <CustomBreadcrumbs crumbs={[{ title: t`Brands`, link: "/brands" }]} />
          </ul>
        </div>

        <Box sx={{ mx: { xs: "15px", md: 0 } }}>
          <Typography
            sx={{
              fontSize: "21px",
              fontWeight: 600,
              mb: 3,
              letterSpacing: "-0.2px",
              lineHeight: "1.1",
            }}
          >
            <Trans>Find Your Favourite Brand</Trans>
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "14px", lineHeight: "20px" }}>
              <Trans>Brand Index:</Trans>
            </Typography>
            <Link
              href="#0-9"
              onClick={(e) => handleScroll(e, "0-9")}
              style={{
                textDecoration: "none",
                color: "black",
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              0 - 9
            </Link>
            {alphabet.map((letter) => (
              <Link
                key={letter}
                href={`#${letter}`}
                onClick={(e) => handleScroll(e, letter)}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  fontSize: "14px",
                  lineHeight: "20px",
                  margin: "0 4px",
                }}
              >
                {letter}
              </Link>
            ))}
          </Box>

          <Box>
            {groupedData && groupedData["0-9"] && (
              <Box sx={{ mb: "20px" }} id="0-9">
                <Typography sx={{ mb: 2, fontSize: "27px", fontWeight: 600, lineHeight: "1.1" }}>
                  0 - 9
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                      lg: "repeat(5, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  {groupedData["0-9"].map((item, index) => (
                    <Box
                      key={getBrandKey(item, index)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                      }}
                    >
                      <Link
                        href={`/brands/${item.manufacturer_id}`}
                        style={{ textDecoration: "none", color: "black" }}
                      >
                        <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
                          {decode(item.name)}
                        </Typography>
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {groupedData &&
              Object.keys(groupedData)
                .filter((key) => key !== "0-9")
                .sort()
                .map((letter) => (
                  <Box key={letter} sx={{ mb: "20px" }} id={letter}>
                    <Typography
                      sx={{ mb: 2, fontSize: "27px", fontWeight: 600, lineHeight: "1.1" }}
                    >
                      {letter}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(3, 1fr)",
                          md: "repeat(4, 1fr)",
                          lg: "repeat(5, 1fr)",
                        },
                        gap: 0.3,
                      }}
                    >
                      {groupedData[letter].map((item, index) => (
                        <Box
                          key={getBrandKey(item, index)}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Link
                            href={`/brands/${item.manufacturer_id}`}
                            style={{ textDecoration: "none", color: "black" }}
                          >
                            <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
                              {decode(item.name)}
                            </Typography>
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
          </Box>
        </Box>
      </div>
    </main>
  );
};

export default BrandsPage;
